<template>
  <div>
    <notification-message class="notification" />

    <div class="layout">
      <!-- Start Filter Intervention Memento -->
      <div class="filterMemento">
        <v-container class="pa-10 pa-0-md">
          <v-card-text class="mb-0 pb-0">{{
            $t("mementoRecord.condition")
          }}</v-card-text>
          <v-form ref="form">
            <v-row class="align-center pt-5">
              <v-col cols="12" md="4" class="py-0">
                <v-menu
                  v-model="datePickerStart"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  class="py-0"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-card-text class="py-0">
                      <v-text-field
                        v-model="filterMemento.dateStart"
                        :label="$t('mementoRecord.dateStartRecord')"
                        prepend-icon="mdi-calendar"
                        readonly
                        clearable
                        color="grey"
                        v-bind="attrs"
                        v-on="on"
                        class="py-0"
                      ></v-text-field>
                    </v-card-text>
                  </template>

                  <v-date-picker
                    v-model="filterMemento.dateStart"
                    :locale="this.$i18n.locale"
                    color="red darken-1"
                    @input="datePickerStart = false"
                    class="py-0"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" class="py-0">
                <v-menu
                  v-model="datePickerEnd"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  class="py-0"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-card-text class="py-0">
                      <v-text-field
                        v-model="filterMemento.dateEnd"
                        :label="$t('mementoRecord.dateEndRecord')"
                        prepend-icon="mdi-calendar"
                        readonly
                        clearable
                        color="grey"
                        v-bind="attrs"
                        v-on="on"
                        class="py-0"
                      ></v-text-field>
                    </v-card-text>
                  </template>

                  <v-date-picker
                    v-model="filterMemento.dateEnd"
                    :locale="this.$i18n.locale"
                    color="red darken-1"
                    @input="datePickerEnd = false"
                    class="py-0"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="2" class="py-0">
                <v-text-field
                  v-model="filterMemento.interventionId"
                  :label="$t('mementoRecord.interventionId')"
                  type="number"
                  min="0"
                  clearable
                  color="grey"
                  class="py-0 pl-12 pl-md-0"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="2" class="py-0 d-flex align-end flex-column">
                <v-btn
                  color="red darken-1"
                  outlined
                  class="py-0"
                  :disabled="$v.filterMemento.$invalid"
                  @click="filter"
                >
                  {{ $t("mementoRecord.filter") }}
                </v-btn>
              </v-col>
            </v-row>

            <v-card-text class="errors mb-0 pb-0" v-if="showErrorMessage">
              {{ errorMessage }}
            </v-card-text>
          </v-form>
        </v-container>
      </div>
      <!-- End Filter Intervention Memento -->

      <!-- Start Array Intervention Memento -->
      <div class="crudArray">
        <v-data-table
          :headers="headers"
          :items="mementos"
          :options.sync="options"
          :server-items-length="totalItemCount"
          :footer-props="{
            'items-per-page-options': [5, 10, 15, 20],
            showFirstLastPage: true,
          }"
          @update:options="updateOptions"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{
                $t("mementoRecord.titleArray")
              }}</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
            </v-toolbar>
          </template>
          <template v-slot:[`item.details`]="{ item }">
            <v-icon small class="mr-2" @click="goToDetails(item)">
              mdi-information
            </v-icon>
          </template>
          <template v-slot:[`item.back`]="{ item }">
            <v-icon small class="mr-2" @click="restore(item)">
              mdi-cached
            </v-icon>
          </template>
        </v-data-table>
      </div>
    </div>
    <!-- End Array Intervention Memento -->
  </div>
</template>

<script>
import { mapActions } from "vuex";
import NotificationMessage from "@/components/components-base/notification/NotificationMessage.vue";
import {
  getAllIMementoHttp,
  getIMementoCountHttp,
  restoreByIdHttp,
  filterIMementoHttp,
  getIMementoFilterCountHttp,
} from "@/services/planning/MementoService.js";
import { validationMixin } from "vuelidate";
import { requiredIf, requiredUnless, required } from "vuelidate/lib/validators";
import i18n from "@/i18n";

export default {
  props: [],
  components: {
    NotificationMessage,
  },
  mixins: [validationMixin],
  validations: {
    filterMemento: {
      dateStart: {
        required,
        maxValue: (value, { dateEnd }) => {
          return new Date(value) < new Date(dateEnd);
        },
      },
      dateEnd: {
        required,
        minValue: (value, { dateStart }) => {
          return new Date(value) > new Date(dateStart);
        },
      },
      interventionId: {
        required,
        minValue: (value) => {
          return value >= 1;
        },
      },
    },
  },
  watch: {},
  computed: {},
  data: () => ({
    search: "",
    mementos: [],
    headers: [
      { text: i18n.t("mementoRecord.hId"), value: "id", align: "center" },
      {
        text: i18n.t("mementoRecord.hdateEndState"),
        value: "dateCreatedState",
        align: "center",
      },
      {
        text: i18n.t("mementoRecord.hEventId"),
        value: "mementoMessage.messageInterventionId",
        align: "center",
      },
      {
        text: i18n.t("mementoRecord.hAction"),
        value: "mementoMessage.messageAction",
      },
      {
        text: i18n.t("mementoRecord.hUserEmail"),
        value: "mementoMessage.userEmail",
      },
      {
        text: i18n.t("mementoRecord.hModifications"),
        value: "mementoMessage.modificationsDone",
      },
      { text: i18n.t("mementoRecord.hDetails"), value: "details" },
      { text: i18n.t("mementoRecord.hRestore"), value: "back" },
    ],
    editedIndex: -1,
    filterMemento: {
      dateStart: null,
      dateEnd: null,
      interventionId: null,
    },
    datePickerStart: false,
    datePickerEnd: false,
    options: {
      page: 1,
      itemsPerPage: 5,
    },
    totalItemCount: 20,
    today: new Date().toISOString().substr(0, 10),
    showErrorMessage: false,
    errorMessage: "",
  }),
  mounted() {
    this.connectToWebsocket();
  },
  created() {
    this.getCount();
    this.getAll();
    this.loadLocations();
    this.loadCourses();
    this.loadUsers();
  },
  destroyed() {
    this.logoutFromWebsocket();
  },
  methods: {
    ...mapActions("websocketModule", [
      "connectToWebsocket",
      "logoutFromWebsocket",
    ]),
    ...mapActions("notificationModule", ["showNotification"]),
    ...mapActions("eventModule", ["editEventNoHttpCall"]),
    ...mapActions("locationModule", ["loadLocations"]),
    ...mapActions("courseModule", ["loadCourses"]),
    ...mapActions("userModule", ["loadUsers"]),
    async getAll() {
      await getAllIMementoHttp(
        this.options.page - 1,
        this.options.itemsPerPage
      ).then((response) => {
        this.mementos = response.data;
      });
    },
    async getCount() {
      await getIMementoCountHttp().then((response) => {
        this.totalItemCount = response.data.nb;
      });
    },
    async getAllFilter() {
      filterIMementoHttp(
        this.filterMemento.interventionId,
        this.filterMemento.dateStart,
        this.filterMemento.dateEnd,
        this.options.page - 1,
        this.options.itemsPerPage
      ).then((response) => {
        this.mementos = response.data;
      });
    },
    async getCountFilter() {
      getIMementoFilterCountHttp(
        this.filterMemento.interventionId,
        this.filterMemento.dateStart,
        this.filterMemento.dateEnd
      ).then((response) => {
        this.totalItemCount = response.data.nb;
      });
    },
    goToDetails(item) {
      this.$router.push({ name: "MementoDetails", params: { id: item.id } });
    },
  /**
   * This method is called after pagination was modified.
   * It will update information in the array depending of the pagination selected
   */
    updateOptions() {
      if (this.$v.filterMemento.$invalid) {
        this.getCount();
        this.getAll();
      } else {
        this.getAllFilter();
        this.getCountFilter();
      }
    },
  /**
   * This method will restore an event from a selected memento.
   */
    async restore(item) {
      await restoreByIdHttp(item.id)
        .then((response) => {
          if (response.status === 200) {
            this.showNotification({
              messageAlert: this.$t("messageStores.mementoRestoreSuccess"),
              typeAlert: "success",
            });
            // update Array
            this.getCount();
            this.getAll();
            this.editEventNoHttpCall(response.data);
          }
        })
        .catch((error) => {
          this.showNotification({
            messageAlert: this.$t("messageStores.mementoRestoreError"),
            typeAlert: "error",
          });
        });
    },
  /**
   * This method will filter data from the array depending of the interventionId, dateStart and dateEnd selected
   */
    filter() {
      if (this.filterMemento.dateStart > this.filterMemento.dateEnd) {
        this.showErrorMessage = true;
        this.errorMessage = this.$t("errorFrontEnd.dateStartBeforeDateEnd");
      } else {
        this.options.page = 1;
        this.getAllFilter();
        this.getCountFilter();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.layout {
  margin: 0 1rem;
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
}

.notification {
  width: 100%;
  height: 100%;
  position: absolute;
  padding: 3% 11%;
}

.crudArray {
  margin: 0 auto 3rem auto;
  width: 80%;
}

.filterMemento {
  background-color: var(--card-color);
  margin: 2rem auto;
  width: 80%;
}

.errors {
  margin: 0;
  color: red !important;
}
</style>
